import React from 'react';
import { Link } from 'react-router-dom';
import { Plane, MapPin, Calendar, Users, Star, ArrowRight } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

const Voyages = () => {
  const { language } = useLanguage();

  const destinations = [
    { 
      name: language === 'fr' ? 'Dubai' : language === 'en' ? 'Dubai' : 'دبي', 
      description: language === 'fr' ? 'Luxe et modernité' : language === 'en' ? 'Luxury and modernity' : 'الفخامة والحداثة', 
      image: 'https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=800',
      flag: '🇦🇪'
    },
    { 
      name: language === 'fr' ? 'Maldives' : language === 'en' ? 'Maldives' : 'المالديف', 
      description: language === 'fr' ? 'Paradis tropical' : language === 'en' ? 'Tropical paradise' : 'جنة استوائية', 
      image: 'https://images.unsplash.com/photo-1514282401047-d79a71a590e8?w=800',
      flag: '🇲🇻'
    },
    { 
      name: language === 'fr' ? 'Paris' : language === 'en' ? 'Paris' : 'باريس', 
      description: language === 'fr' ? 'Ville lumière' : language === 'en' ? 'City of light' : 'مدينة النور', 
      image: 'https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=800',
      flag: '🇫🇷'
    },
    { 
      name: language === 'fr' ? 'Tokyo' : language === 'en' ? 'Tokyo' : 'طوكيو', 
      description: language === 'fr' ? 'Tradition et innovation' : language === 'en' ? 'Tradition and innovation' : 'التقليد والابتكار', 
      image: 'https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?w=800',
      flag: '🇯🇵'
    },
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="voyages-page">
      {/* Hero Section - Couleurs maquette */}
      <section className="relative h-[600px] overflow-hidden">
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
          data-testid="voyages-hero-video"
        >
          <source
            src="https://videos.pexels.com/video-files/7429830/7429830-hd_1920_1080_25fps.mp4"
            type="video/mp4"
          />
          <source
            src="https://cdn.pixabay.com/video/2020/03/15/33195-400327464_large.mp4"
            type="video/mp4"
          />
        </video>
        
        <div className="absolute inset-0 bg-gradient-to-r from-maquette-dark/80 to-maquette-blue/60"></div>
        
        <div className="relative z-10 container mx-auto px-4 lg:px-8 h-full flex items-center">
          <div className="max-w-3xl text-white">
            <div className="flex items-center space-x-2 mb-4">
              <Plane size={32} />
              <span className="font-semibold text-lg">
                {language === 'fr' && 'Agence de Voyage'}
                {language === 'en' && 'Travel Agency'}
                {language === 'ar' && 'وكالة السفر'}
              </span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6" data-testid="voyages-title">
              {language === 'fr' && 'Explorez le monde avec nous'}
              {language === 'en' && 'Explore the world with us'}
              {language === 'ar' && 'استكشف العالم معنا'}
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-gray-100">
              {language === 'fr' && 'Des expériences de voyage uniques, personnalisées selon vos envies et votre budget.'}
              {language === 'en' && 'Unique travel experiences, personalized according to your desires and budget.'}
              {language === 'ar' && 'تجارب سفر فريدة، مخصصة حسب رغباتك وميزانيتك.'}
            </p>
            <Link
              to="/contact"
              data-testid="voyages-cta"
              className="inline-flex items-center space-x-2 bg-white text-maquette-blue px-8 py-4 rounded-md font-semibold hover:bg-gray-100 transition-all shadow-lg"
            >
              <span>
                {language === 'fr' && 'Planifier mon voyage'}
                {language === 'en' && 'Plan my trip'}
                {language === 'ar' && 'خطط لرحلتي'}
              </span>
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Services Section - Couleurs maquette */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-maquette-text mb-4">
              {language === 'fr' && 'Nos services de voyage'}
              {language === 'en' && 'Our travel services'}
              {language === 'ar' && 'خدمات السفر لدينا'}
            </h2>
            <p className="text-xl text-maquette-gray">
              {language === 'fr' && 'Une expertise complète pour tous vos besoins'}
              {language === 'en' && 'Complete expertise for all your needs'}
              {language === 'ar' && 'خبرة كاملة لجميع احتياجاتك'}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-lg hover:shadow-xl transition-shadow border-t-4 border-maquette-blue">
              <div className="bg-maquette-blue/10 rounded-full w-14 h-14 flex items-center justify-center mb-4">
                <Users className="text-maquette-blue" size={28} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-maquette-text">
                {language === 'fr' && 'Circuits privés'}
                {language === 'en' && 'Private tours'}
                {language === 'ar' && 'جولات خاصة'}
              </h3>
              <p className="text-maquette-gray leading-relaxed">
                {language === 'fr' && 'Voyages personnalisés pour groupes et familles, avec guide privé et itinéraires sur mesure.'}
                {language === 'en' && 'Personalized trips for groups and families, with private guide and custom itineraries.'}
                {language === 'ar' && 'رحلات مخصصة للمجموعات والعائلات، مع دليل خاص ومسارات مخصصة.'}
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-lg hover:shadow-xl transition-shadow border-t-4 border-maquette-blue">
              <div className="bg-maquette-blue/10 rounded-full w-14 h-14 flex items-center justify-center mb-4">
                <Calendar className="text-maquette-blue" size={28} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-maquette-text">
                {language === 'fr' && 'Voyages d\'affaires'}
                {language === 'en' && 'Business travel'}
                {language === 'ar' && 'السفر التجاري'}
              </h3>
              <p className="text-maquette-gray leading-relaxed">
                {language === 'fr' && 'Organisation complète de vos déplacements professionnels : hôtels, transport, réunions.'}
                {language === 'en' && 'Complete organization of your business trips: hotels, transport, meetings.'}
                {language === 'ar' && 'تنظيم كامل لرحلات عملك: فنادق، نقل، اجتماعات.'}
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-lg hover:shadow-xl transition-shadow border-t-4 border-maquette-blue">
              <div className="bg-maquette-blue/10 rounded-full w-14 h-14 flex items-center justify-center mb-4">
                <Star className="text-maquette-blue" size={28} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-maquette-text">
                {language === 'fr' && 'Team Building'}
                {language === 'en' && 'Team Building'}
                {language === 'ar' && 'بناء الفريق'}
              </h3>
              <p className="text-maquette-gray leading-relaxed">
                {language === 'fr' && 'Séminaires et activités de groupe à l\'international pour renforcer la cohésion d\'équipe.'}
                {language === 'en' && 'International seminars and group activities to strengthen team cohesion.'}
                {language === 'ar' && 'ندوات وأنشطة جماعية دولية لتعزيز تماسك الفريق.'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Video Showcase Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-maquette-text mb-4">
              {language === 'fr' && 'Nos destinations en vidéo'}
              {language === 'en' && 'Our destinations in video'}
              {language === 'ar' && 'وجهاتنا بالفيديو'}
            </h2>
            <p className="text-xl text-maquette-gray">
              {language === 'fr' && 'Découvrez la beauté de nos destinations'}
              {language === 'en' && 'Discover the beauty of our destinations'}
              {language === 'ar' && 'اكتشف جمال وجهاتنا'}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            <div className="rounded-xl overflow-hidden shadow-lg bg-white" data-testid="destination-video-1">
              <video
                controls
                className="w-full h-full object-cover"
                poster="https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=800"
              >
                <source
                  src="https://cdn.pixabay.com/video/2019/09/05/26299-359787832_large.mp4"
                  type="video/mp4"
                />
              </video>
              <div className="p-4">
                <h3 className="text-xl font-bold text-maquette-text">
                  {language === 'fr' && 'Destinations Exotiques'}
                  {language === 'en' && 'Exotic Destinations'}
                  {language === 'ar' && 'وجهات غريبة'}
                </h3>
                <p className="text-maquette-gray">
                  {language === 'fr' && 'Plages paradisiaques et paysages magnifiques'}
                  {language === 'en' && 'Paradise beaches and magnificent landscapes'}
                  {language === 'ar' && 'شواطئ الجنة ومناظر طبيعية رائعة'}
                </p>
              </div>
            </div>

            <div className="rounded-xl overflow-hidden shadow-lg bg-white" data-testid="destination-video-2">
              <video
                controls
                className="w-full h-full object-cover"
                poster="https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=800"
              >
                <source
                  src="https://cdn.pixabay.com/video/2021/08/24/86649-593824976_large.mp4"
                  type="video/mp4"
                />
              </video>
              <div className="p-4">
                <h3 className="text-xl font-bold text-maquette-text">
                  {language === 'fr' && 'Villes Modernes'}
                  {language === 'en' && 'Modern Cities'}
                  {language === 'ar' && 'مدن حديثة'}
                </h3>
                <p className="text-maquette-gray">
                  {language === 'fr' && 'Architecture impressionnante et culture urbaine'}
                  {language === 'en' && 'Impressive architecture and urban culture'}
                  {language === 'ar' && 'هندسة معمارية مذهلة وثقافة حضرية'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Destinations Grid */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-maquette-text mb-4">
              {language === 'fr' && 'Destinations populaires'}
              {language === 'en' && 'Popular destinations'}
              {language === 'ar' && 'وجهات شعبية'}
            </h2>
            <p className="text-xl text-maquette-gray">
              {language === 'fr' && 'Sélectionnez votre prochaine aventure'}
              {language === 'en' && 'Select your next adventure'}
              {language === 'ar' && 'اختر مغامرتك القادمة'}
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            {destinations.map((dest, index) => (
              <div
                key={index}
                className="group relative overflow-hidden rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 cursor-pointer"
              >
                <img
                  src={dest.image}
                  alt={dest.name}
                  className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-maquette-dark/90 to-transparent flex flex-col justify-end p-6">
                  <div className="flex items-center space-x-2 text-white mb-2">
                    <span className="text-2xl">{dest.flag}</span>
                    <h3 className="text-2xl font-bold">{dest.name}</h3>
                  </div>
                  <p className="text-gray-200">{dest.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section - Couleurs maquette */}
      <section className="py-20 bg-gradient-to-r from-maquette-blue to-maquette-dark text-white">
        <div className="container mx-auto px-4 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-4">
            {language === 'fr' && 'Prêt pour l\'aventure ?'}
            {language === 'en' && 'Ready for adventure?'}
            {language === 'ar' && 'هل أنت مستعد للمغامرة؟'}
          </h2>
          <p className="text-xl mb-8 text-gray-100">
            {language === 'fr' && 'Contactez-nous pour créer votre voyage sur mesure'}
            {language === 'en' && 'Contact us to create your custom trip'}
            {language === 'ar' && 'اتصل بنا لإنشاء رحلتك المخصصة'}
          </p>
          <Link
            to="/contact"
            data-testid="voyages-contact-cta"
            className="inline-flex items-center space-x-2 bg-white text-maquette-blue px-8 py-4 rounded-md font-semibold hover:bg-gray-100 transition-all shadow-lg"
          >
            <span>
              {language === 'fr' && 'Demander un devis'}
              {language === 'en' && 'Request a quote'}
              {language === 'ar' && 'طلب عرض سعر'}
            </span>
            <ArrowRight size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Voyages;